import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, FileText, Clock, ExternalLink, Calendar,
  BookOpen, Download, ChevronRight
} from "lucide-react";

const PREVIOUS_YEAR_PAPERS = [
  {
    exam: "IBPS PO",
    papers: [
      { year: "2024", type: "Prelims", sections: ["Quant", "Reasoning", "English"], questions: 100, duration: "60 min" },
      { year: "2024", type: "Mains", sections: ["Reasoning & CA", "English", "Data Analysis", "Banking Awareness"], questions: 155, duration: "180 min" },
      { year: "2023", type: "Prelims", sections: ["Quant", "Reasoning", "English"], questions: 100, duration: "60 min" },
      { year: "2023", type: "Mains", sections: ["Reasoning & CA", "English", "Data Analysis", "Banking Awareness"], questions: 155, duration: "180 min" },
    ]
  },
  {
    exam: "SBI PO",
    papers: [
      { year: "2024", type: "Prelims", sections: ["Quant", "Reasoning", "English"], questions: 100, duration: "60 min" },
      { year: "2024", type: "Mains", sections: ["Reasoning & CA", "Data Analysis", "Banking Awareness", "English"], questions: 155, duration: "180 min" },
      { year: "2023", type: "Prelims", sections: ["Quant", "Reasoning", "English"], questions: 100, duration: "60 min" },
    ]
  },
  {
    exam: "IBPS Clerk",
    papers: [
      { year: "2024", type: "Prelims", sections: ["Numerical", "Reasoning", "English"], questions: 100, duration: "60 min" },
      { year: "2023", type: "Prelims", sections: ["Numerical", "Reasoning", "English"], questions: 100, duration: "60 min" },
    ]
  },
  {
    exam: "RBI Grade B",
    papers: [
      { year: "2024", type: "Phase I", sections: ["Quant", "Reasoning", "English", "GA"], questions: 200, duration: "120 min" },
      { year: "2023", type: "Phase I", sections: ["Quant", "Reasoning", "English", "GA"], questions: 200, duration: "120 min" },
    ]
  },
];

const BankingPreviousYearsPage = () => {
  const navigate = useNavigate();
  const [expandedExam, setExpandedExam] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/banking")} data-testid="pyp-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Previous Year Papers</h1>
            <p className="text-sm text-slate-500">Analyze patterns from past banking exams</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <BookOpen className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Pattern Analysis</p>
            <p className="text-sm text-amber-700">Use these papers to understand exam patterns, question distribution, and difficulty levels. Practice with our Mock Tests for the real exam experience.</p>
          </div>
        </div>

        <div className="space-y-4">
          {PREVIOUS_YEAR_PAPERS.map((examGroup) => (
            <Card key={examGroup.exam} className="overflow-hidden" data-testid={`pyp-${examGroup.exam.replace(/\s/g, '-').toLowerCase()}`}>
              <button
                className="w-full p-5 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedExam(expandedExam === examGroup.exam ? null : examGroup.exam)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{examGroup.exam}</h3>
                    <p className="text-xs text-slate-500">{examGroup.papers.length} papers available</p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedExam === examGroup.exam ? 'rotate-90' : ''}`} />
              </button>

              {expandedExam === examGroup.exam && (
                <div className="border-t border-slate-100 px-5 pb-5">
                  <div className="space-y-3 mt-3">
                    {examGroup.papers.map((paper, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-center">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-xs font-bold text-slate-700 mt-0.5">{paper.year}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{examGroup.exam} {paper.type} {paper.year}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {paper.questions}Q</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {paper.duration}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {paper.sections.map((s) => (
                                <Badge key={s} variant="outline" className="text-xs py-0">{s}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (paper.type === "Prelims") navigate("/banking/mock-test");
                            else navigate("/banking/mains-mock-test");
                          }}
                          data-testid={`practice-${examGroup.exam.replace(/\s/g, '-').toLowerCase()}-${paper.year}-${paper.type.toLowerCase()}`}
                        >
                          Practice
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-slate-600">More papers will be added soon. Practice with our <strong>Mock Tests</strong> that mirror real exam patterns.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankingPreviousYearsPage;
